'use client';

import Link from 'next/link';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/atoms/primitives/sheet';
import { Button } from '@/components/atoms/Button';
import { buildSignupWithRedirect } from '@/lib/authRedirect';
import { crossHostSignup } from '@/lib/cross-host-redirect';

export interface FeaturePreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  ctaLabel: string;
  /** Destination when `returnPathAfterAuth` is not set. Default `/signup`. */
  ctaHref?: string;
  /**
   * Current app path to return to after signup (e.g. `/map?category=music&lat=...`).
   * When set, overrides `ctaHref` with `/signup?redirect=...`.
   */
  returnPathAfterAuth?: string;
}

export function FeaturePreviewSheet({
  open,
  onOpenChange,
  title,
  description,
  ctaLabel,
  ctaHref,
  returnPathAfterAuth,
}: FeaturePreviewSheetProps) {
  const resolvedCtaHref = ctaHref ?? crossHostSignup();
  const href = returnPathAfterAuth ? buildSignupWithRedirect(returnPathAfterAuth) : resolvedCtaHref;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" title={null} className="rounded-t-2xl px-6 pb-8">
        <SheetHeader className="items-center text-center">
          <SheetTitle className="text-lg">{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex justify-center">
          <Button asChild variant="accent" size="lg">
            <Link href={href}>{ctaLabel}</Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
